import { useState, useEffect } from "react";
import { Deck } from "@/lib/classes/deck";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { UnitCategory } from "@/lib/types/deck-builder";
import type { DeckUnit, Division, UnitMap } from "@/lib/types/deck-builder";
import { ArmouryView } from "./ArmouryView";
import { DeckView } from "./DeckView";
import { exportDeckToCode } from "@/lib/utils/deck-code";
import { Copy, Check, Layout, Save } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { getDivisionImagePath, getCoalitionFlagPath, getCountryFlagPath } from "@/lib/utils/division-images";
import { DeckFormModal } from "@/components/deck-library/DeckFormModal";
import { createDeck, updateDeck } from "@/lib/api/deck-api";
import type { CreateDeckData, UpdateDeckData } from "@/lib/api/deck-api";
import { useAuth } from "@/lib/store/auth-store";
import { toast } from "sonner";

interface EditDeckProps {
  deck: Deck;
  divisions: Division[];
  unitMap: UnitMap;
  deckId?: number;
  onSummaryClick?: () => void;
  onChangeDivision?: () => void;
  onDeckCleared?: () => void;
}

export function EditDeck({ deck, divisions, unitMap, deckId, onSummaryClick, onChangeDivision, onDeckCleared }: EditDeckProps) {
  const [selectedCategory, setSelectedCategory] = useState<UnitCategory | null>(null);
  const [, setUpdateTrigger] = useState(0);
  const [copied, setCopied] = useState(false);
  const [deckDrawerOpen, setDeckDrawerOpen] = useState(false);
  const { user, loading: loadingUser } = useAuth();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedDeckCode, setSavedDeckCode] = useState<string>("");

  useEffect(() => {
    const unsubscribe = deck.onChange(() => {
      setUpdateTrigger((prev) => prev + 1);
    });
    return unsubscribe;
  }, [deck]);

  const handleAddUnit = (pack: any, veterancy: number = 0, transport?: any) => {
    const deckUnit: DeckUnit = {
      pack,
      veterancy,
      transport,
    };
    deck.addUnit(deckUnit);
  };

  const handleRemoveUnit = (unit: DeckUnit) => {
    deck.removeUnit(unit);
  };

  const handleUpdateUnit = (oldUnit: DeckUnit, newUnit: DeckUnit) => {
    // Find the index of the old unit to preserve its position
    const unitIndex = deck.units.findIndex((unit) => unit === oldUnit);
    if (unitIndex === -1) {
      // If not found, just add it (fallback)
      deck.removeUnit(oldUnit);
      deck.addUnit(newUnit);
      return;
    }
    
    // Remove old unit
    deck.removeUnit(oldUnit);
    
    // Insert new unit at the same position
    const updatedUnits = [...deck.units];
    updatedUnits.splice(unitIndex, 0, newUnit);
    deck.units = updatedUnits;
  };

  const handleExportDeck = async () => {
    try {
      await exportDeckToCode(deck);
      setCopied(true);
      toast.success("Deck code copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to export deck:", error);
      toast.error("Failed to copy deck code to clipboard");
    }
  };

  const handleSaveToLibrary = () => {
    // Check if user is authenticated
    if (!user?.discordId) {
      toast.error("Please log in to save decks to your library.");
      return;
    }

    try {
      // Generate deck code and store it before opening modal
      const deckCode = deck.toDeckCode();
      setSavedDeckCode(deckCode);
      setShowSaveModal(true);
    } catch (error) {
      console.error("Failed to generate deck code:", error);
      toast.error("Failed to generate deck code");
    }
  };

  const handleCreateDeck = async (data: CreateDeckData | UpdateDeckData) => {
    try {
      if (deckId) {
        // Update existing deck
        await updateDeck(deckId, data as UpdateDeckData);
        toast.success("Deck updated successfully!");
      } else {
        // Create new deck
        await createDeck(data as CreateDeckData);
        toast.success("Deck saved to library successfully!");
      }
      // On success, close modal
      setShowSaveModal(false);
    } catch (error) {
      // Re-throw to let the form handle the error
      throw error;
    }
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex items-center justify-between p-2 sm:p-4 border-b gap-2 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <img
            src={getDivisionImagePath(deck.division.descriptor)}
            alt={deck.division.name}
            className="h-8 sm:h-10 lg:h-12 w-auto object-contain flex-shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <img
              src={getCoalitionFlagPath(deck.division.alliance)}
              alt={deck.division.alliance}
              className="h-5 sm:h-6 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <img
              src={getCountryFlagPath(deck.division.country)}
              alt={deck.division.country}
              className="h-5 sm:h-6 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <h2 className="text-base sm:text-xl lg:text-2xl font-bold truncate min-w-0">{deck.division.name}</h2>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Mobile: View Deck button */}
          <Button 
            onClick={() => setDeckDrawerOpen(true)} 
            variant="outline" 
            size="sm"
            className="lg:hidden"
            aria-label="View Deck"
          >
            <Layout className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Deck</span>
          </Button>
          
          {/* Desktop buttons */}
          <div className="hidden lg:flex gap-2">
          <Button 
            onClick={handleSaveToLibrary} 
            variant="default" 
            size="sm"
            disabled={loadingUser || !user?.discordId}
            title={!user?.discordId ? "Please log in to save decks" : undefined}
          >
            <Save className="h-4 w-4 mr-2" />
            {deckId ? "Update Deck" : "Save to Library"}
          </Button>
          <Button onClick={handleExportDeck} variant="outline" size="sm">
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Export Code
              </>
            )}
          </Button>
          {onSummaryClick && (
              <Button onClick={onSummaryClick} variant="outline" size="sm">
              Summary
            </Button>
          )}
          {onChangeDivision && (
              <Button onClick={onChangeDivision} variant="outline" size="sm">
              Change Division
            </Button>
          )}
          </div>
          
          {/* Mobile: Save to Library button */}
          <Button 
            onClick={handleSaveToLibrary} 
            variant="default" 
            size="sm"
            className="lg:hidden"
            aria-label={deckId ? "Update Deck" : "Save to Library"}
            disabled={loadingUser || !user?.discordId}
            title={!user?.discordId ? "Please log in to save decks" : undefined}
          >
            <Save className="h-4 w-4" />
          </Button>
          {/* Mobile: Export button (icon only) */}
          <Button 
            onClick={handleExportDeck} 
            variant="outline" 
            size="sm"
            className="lg:hidden"
            aria-label={copied ? "Copied!" : "Export Code"}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:flex-row">
        {/* Deck View - Left Side (desktop only) */}
        <div className="hidden min-h-0 min-w-0 lg:flex lg:w-[360px] xl:w-[400px] 2xl:w-[450px] lg:max-w-[450px] lg:flex-shrink-0 flex flex-col overflow-hidden border-r">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Deck</h3>
              {onDeckCleared && (
                <Button onClick={onDeckCleared} variant="destructive" size="sm">
                  Clear
                </Button>
              )}
            </div>
          </div>
                <div className="min-h-0 min-w-0 flex-1 overflow-hidden p-4">
                  <DeckView deck={deck} onRemoveUnit={handleRemoveUnit} onUpdateUnit={handleUpdateUnit} />
                </div>
        </div>

        {/* Armoury View - Main content (full width on mobile, remaining on desktop) */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="p-2 sm:p-4 border-b">
            <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-4">Available Units</h3>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(null)}
                className="text-xs sm:text-sm"
              >
                All
              </Button>
              {deck.unitCategories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="text-xs sm:text-sm"
                >
                  {category.split("/").pop()}
                </Button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto p-2 sm:p-4">
            <ArmouryView deck={deck} selectedCategory={selectedCategory} onAddUnit={handleAddUnit} />
          </div>
        </div>
      </div>

      {/* Mobile Deck Drawer */}
      <Sheet open={deckDrawerOpen} onOpenChange={setDeckDrawerOpen}>
        <SheetContent side="left" className="flex min-w-0 w-[90vw] flex-col overflow-hidden p-0 sm:w-[400px]">
          <SheetHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b pr-12 sm:pr-14 flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg sm:text-xl font-semibold">Deck</SheetTitle>
              {onDeckCleared && (
                <Button onClick={onDeckCleared} variant="destructive" size="sm">
                  Clear
                </Button>
              )}
            </div>
          </SheetHeader>
          <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6">
            <DeckView deck={deck} onRemoveUnit={handleRemoveUnit} onUpdateUnit={handleUpdateUnit} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Save to Library Modal */}
      {user?.discordId && (
        <DeckFormModal
          open={showSaveModal}
          onOpenChange={(open) => {
            setShowSaveModal(open);
            // Clear saved deck code when modal closes (after successful save)
            if (!open) {
              setSavedDeckCode("");
            }
          }}
          deck={null}
          deckId={deckId}
          initialDeckCode={savedDeckCode}
          divisions={divisions}
          unitMap={unitMap}
          discordUsername={user.name}
          isVerified={user.isVerified || false}
          onSubmit={handleCreateDeck}
        />
      )}
    </div>
  );
}

